/*eslint-disable*/
// #ux-redesign-start: Sidebar reescrito (Paso 3 — Patients)
// Adaptación del patrón Web-Doctors. Cambios vs el legacy:
//   - Agrupación por section (declarada en routes.js: section: 'hub'|...).
//   - Rail mode (64px collapsed) con toggle persistido en localStorage
//     (vía useSidebarCollapsed).
//   - Headers de sección como overline (text-tertiary).
//   - Item activo: bg surface-sunken + border-left brand-500.
//   - Mobile (<992px): drawer overlay desde la izquierda (controlado por
//     la clase `nav-open` que setea AdminNavbar).
//   - Theme-aware: lee tokens, light + dark.
//   - Logout dispara <ConfirmDialog> del DS (vía actionShowLogoutModal).
//
// Diferencias vs Doctors:
//   - No usa useCategoryMenu (no existe en Patients).
//   - No usa useAuthorizationValidator/practiceManage (Patients no tiene practices).
//   - Filtros: invisible + password_expired/on_delete + requiresInitialChoice.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'perfect-scrollbar';

import { actionShowLogoutModal } from 'store/actions/usersActions';
import Icon from 'components/Icons/Icon';
import { useFileUtils } from 'hooks';
import { useTranslate } from 'react-translate';
import useSidebarCollapsed from 'hooks/useSidebarCollapsed';
import { groupRoutesBySection } from './sections';

var ps;

const Sidebar = (props) => {
  const sidebar = useRef();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.userReducer);
  const { profile } = useSelector((state) => state.profilesReducer);
  const { mobile } = useSelector((state) => state.commonReducer);

  const avatarId = typeof document !== 'undefined' ? document.getElementById('user-avatar') : null;
  const { getAvatar, defaultImage } = useFileUtils(avatarId);
  const { collapsed, toggle: toggleCollapsed } = useSidebarCollapsed();
  const t = useTranslate('sidebar');
  const ta = useTranslate('a11y');

  const [requiresInitialChoice, setRequired] = useState(
    typeof localStorage !== 'undefined' && localStorage.getItem('pending_initial_choice') != null
  );

  const isMobile = mobile;
  const isCollapsed = !isMobile && collapsed;

  // Avatar refresh
  useEffect(() => {
    getAvatar(user.avatar, 'large', true);
  }, [user.avatar, user.updated_at]);

  // Perfect-scrollbar Windows
  useEffect(() => {
    if (navigator.platform.indexOf('Win') > -1 && sidebar.current) {
      ps = new PerfectScrollbar(sidebar.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
    }
    const onStorage = (e) => {
      if (!e.pending_initial_choice) setRequired(null);
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      if (navigator.platform.indexOf('Win') > -1 && ps) ps.destroy();
      var id = window.setTimeout(null, 0);
      while (id--) window.clearTimeout(id);
    };
  }, [Object.keys(user).length]);

  const closeMobileDrawer = () => {
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove('nav-open');
      const toggle = document.querySelector('.navbar-toggle');
      if (toggle) toggle.classList.remove('toggled');
    }
  };

  const handleItemClick = () => {
    // #ux-redesign: reset scroll en cualquier click del sidebar — garantiza que
    // si el usuario volvió a la misma sección desde un detalle scrolleado, la
    // vista arranque arriba aunque el pathname no cambie.
    if (typeof window !== 'undefined' && window.scrollTo) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    if (typeof document !== 'undefined') {
      const fullPage = document.getElementById('full-page');
      if (fullPage && typeof fullPage.scroll === 'function') {
        fullPage.scroll({ top: 0, behavior: 'auto' });
      }
    }
    if (isMobile) {
      closeMobileDrawer();
    }
  };

  const isRouteActive = (routePath) => {
    if (routePath === '/admin/frametest/:doctor?') {
      return window.location.href.indexOf('/admin/frametest') > -1;
    }
    const path = routePath.replace(/(\:)\S{1,}/, '');
    return window.location.href.indexOf(path) > -1;
  };

  const filterVisibleRoutes = (routes) =>
    routes.filter((prop) => {
      if (prop.invisible) return false;
      if (prop.collapse) return false; // Patients tiene grupos demo collapsed — ya invisibles
      return true;
    });

  const renderItem = ({ to, icon, label, active, name, key, onClick }) => (
    <li
      key={key}
      className={`ux-side__item ${active ? 'ux-side__item--active' : ''}`}
      onClick={onClick || handleItemClick}
      name={name}
    >
      <NavLink to={to} activeClassName="" className="ux-side__link">
        {icon && (
          <span className="ux-side__icon" aria-hidden="true">
            <i className={icon} />
          </span>
        )}
        <span className="ux-side__label">{label}</span>
      </NavLink>
    </li>
  );

  const renderRoute = (prop) => {
    const to = prop.layout + prop.path;
    const i18nKey = prop.name ? prop.name.toLowerCase().replace(/ /g, '-') : '';
    const label = i18nKey ? t(i18nKey) : prop.name;
    return renderItem({
      key: `r-${prop._id || prop.path}`,
      to,
      icon: prop.icon,
      label,
      active: isRouteActive(to),
      name: `sidebar_${prop.key || prop._id || ''}`,
    });
  };

  const buildGroups = () => {
    const guardOK =
      !user.password_expired && !user.on_delete && profile.is_ready && !requiresInitialChoice;
    if (!guardOK) return [];
    const visible = filterVisibleRoutes(props.routes);
    return groupRoutesBySection(visible);
  };

  const groups = buildGroups();

  return (
    <>
      {/* #ux-redesign: backdrop visible sólo en mobile + nav-open */}
      <div
        className="ux-sidebar__overlay"
        onClick={closeMobileDrawer}
        aria-hidden="true"
      />
      <aside
        className={`ux-sidebar ${isCollapsed ? 'ux-sidebar--collapsed' : ''} sidebar sidebar-ts`}
        data-color={props.backgroundColor}
        aria-label={ta('main-nav')}
      >
        {/* Logo / Brand */}
        <div className="ux-sidebar__brand">
          <div className="ux-sidebar__logo">
            <Icon file="logo_TP_initials.svg" alt="TelePraxen" width="28px" />
          </div>
          <span className="ux-sidebar__brand-name">TelePraxen</span>
          <button
            type="button"
            className="ux-sidebar__collapse-btn"
            onClick={isMobile ? closeMobileDrawer : toggleCollapsed}
            aria-label={
              isMobile
                ? 'Schließen'
                : isCollapsed
                ? 'Erweitern'
                : 'Einklappen'
            }
            title={isMobile ? 'Schließen' : isCollapsed ? 'Erweitern' : 'Einklappen'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {isMobile ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : isCollapsed ? (
                <polyline points="9 18 15 12 9 6" />
              ) : (
                <polyline points="15 18 9 12 15 6" />
              )}
            </svg>
          </button>
        </div>

        {/* Cuerpo scrolleable */}
        <div className="ux-sidebar__body" id="sidebar" ref={sidebar}>
          {/* User card — entrada al Profile */}
          <div
            className="ux-sidebar__user"
            onClick={() => {
              // #ux-redesign: cerrar drawer mobile + scroll reset + navegar
              handleItemClick();
              if (props.history) props.history.push('/admin/profile');
            }}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && props.history) {
                e.preventDefault();
                handleItemClick();
                props.history.push('/admin/profile');
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={t('account')}
            title={
              user.firstName
                ? `${user.firstName} ${user.lastName} · ${t('account')}`
                : t('account')
            }
          >
            <div className="ux-sidebar__avatar">
              <img src={defaultImage} alt="Avatar" id="user-avatar" />
            </div>
            <div className="ux-sidebar__user-info">
              <span className="ux-sidebar__user-name">
                {user.isAuth && `${user.firstName || ''} ${user.lastName || ''}`.trim()}
              </span>
              {user.email && (
                <span className="ux-sidebar__user-email">{user.email}</span>
              )}
            </div>
            <span className="ux-sidebar__user-cue" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </span>
          </div>

          {/* Grupos por sección */}
          <nav className="ux-sidebar__nav">
            {groups.map(({ section, routes }) => (
              <div className="ux-side__group" key={section.key}>
                {!isCollapsed && (
                  <div className="ux-side__group-header">
                    {t(section.i18nKey) || section.i18nKey}
                  </div>
                )}
                <ul className="ux-side__list">
                  {routes.map((r) => renderRoute(r))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* Logout fijo al fondo */}
        <div className="ux-sidebar__footer">
          <button
            type="button"
            className="ux-side__logout"
            onClick={() => dispatch(actionShowLogoutModal(true))}
            aria-label={t('logout')}
          >
            <span className="ux-side__icon" aria-hidden="true">
              <i className="icon-logout" />
            </span>
            <span className="ux-side__label">{t('logout')}</span>
          </button>
        </div>

        {/* Debug (oculto en demo/preprod) */}
        <div
          className={
            process.env.REACT_APP_ENV_NAME == 'demo' || process.env.REACT_APP_ENV_NAME == 'preprod'
              ? 'd-none'
              : 'ux-sidebar__debug'
          }
        >
          <small className="text-truncate">{process.env.REACT_APP_HOST}</small>
        </div>
      </aside>
    </>
  );
};

Sidebar.defaultProps = {
  routes: [],
  showNotification: false,
  backgroundColor: 'blue',
  minimizeSidebar: () => {},
  hideSidebar: () => {},
};

Sidebar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  showNotification: PropTypes.bool,
  backgroundColor: PropTypes.oneOf(['blue', 'yellow', 'green', 'orange', 'red']),
  minimizeSidebar: PropTypes.func,
  hideSidebar: PropTypes.func,
};

export default React.memo(Sidebar);
// #ux-redesign-end
